import { Box, Button, Typography, Chip, Drawer, IconButton, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../../redux/slices/profileSlice';
import CustomModal from '../../CustomModal';
import useResponsive from '../../../hooks/useResponsive';
import Iconify from '../../Iconify';
import RedLight from '../../../assets/profile/red.png';
import BlueLight from '../../../assets/profile/blue.png';
import YellowLight from '../../../assets/profile/yellow.png';
import AddBankAccountDialog from './AddBankAccountDialog';
import DeleteBankAccount from './DeleteBankAccount';
import useLocales from '../../../hooks/useLocales';
import Image from '../../Image';
import IconPlus from '../../../assets/icon-plus.png';
import { isSafariOrIOS } from '../../../utils/isSafariOrIOS';

const AddBankButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '30px',
  fontWeight: 600,
  fontSize: '16px',
  color: '#1a1a1a',
  background: theme.palette.gradients.buttonPrimary,
  boxShadow: 'none',
  '&:hover': {
    background: theme.palette.gradients.buttonPrimary,
    boxShadow: 'none',
  },
}));

const EditDeleteButton = styled(Button)(({ theme }) => ({
  borderRadius: '30px',
  fontWeight: 600,
  textTransform: 'none',
  background: '#fff',
  color: `${theme.palette.primary.dark} !important`,
  border: `2px solid ${theme.palette.primary.dark}`,
}));

/* ---------------- BANK CARD COMPONENT ---------------- */
const BankCard = ({ bank, background }) => {
  const [visible, setVisible] = useState(false);

  const maskAccountNumber = (num) => {
    if (!num) return '';
    return `****${num.slice(-4)}`;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '14px',
        overflow: 'hidden',
        height: 110,
        flexShrink: 0, // ⭐ prevents card from resizing
        p: 2,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      {/* Background */}
      <Box
        component="img"
        src={background}
        alt="bank background"
        sx={{
          position: 'absolute',
          inset: 0, // ⭐ shorthand for top/left/right/bottom
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Default Chip */}
      {bank.isDefault && (
        <Chip
          label="Default"
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            background: '#fff',
            fontWeight: 600,
            zIndex: 2,
          }}
        />
      )}

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography fontWeight={600} fontSize="14px">
          {bank.name}
        </Typography>

        <Typography fontSize="12px" sx={{ opacity: 0.9 }}>
          {bank.holder}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
          <Typography fontSize="18px" fontWeight={700} letterSpacing="2px">
            {visible ? bank.rawNumber : maskAccountNumber(bank.rawNumber)}
          </Typography>

          <IconButton size="small" onClick={() => setVisible(!visible)} sx={{ color: '#fff' }}>
            <Iconify icon={visible ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} width={20} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

const BankDetailsDialog = ({ handleClose, handleOpen }) => {
  const { translate } = useLocales();
  const dispatch = useDispatch();
  const isDesktop = useResponsive('up', 'lg');
  const [openBankAccount, setOpenBankAccount] = useState(false);
  const [openDeleteBankAccount, setOpenDeleteBankAccount] = useState(false);
  const theme = useTheme();
  const { memberInfo } = useSelector((x) => ({
    ...x.providers,
    ...x.lookup,
    ...x.game,
  }));

  const imageGradient = [YellowLight, BlueLight, RedLight];

  /* ---------------- BANK LIST ---------------- */

  const banks = useMemo(() => {
    return (memberInfo?.userBank || [])
      .map((bank) => ({
        id: bank.id,
        name: bank.bank_name,
        holder: bank.account_holder_name,
        rawNumber: bank.account_number,
        isDefault: bank.is_default === 1,
      }))
      .sort((a, b) => b.isDefault - a.isDefault);
  }, [memberInfo?.userBank]);

  return (
    <>
      {isDesktop && (
        <CustomModal
          hasClose
          maxWidth="xs"
          open={handleOpen}
          onClose={handleClose}
          actions={
            <Box display="flex" flexDirection="column" width="100%" gap={2}>
              <AddBankButton
                onClick={() =>
                  dispatch(
                    openModal({
                      view: 'add-bank-account',
                    })
                  )
                }
                fullWidth
              >
                {translate('add_bank_account')}
              </AddBankButton>

              <EditDeleteButton
                onClick={() =>
                  dispatch(
                    openModal({
                      view: 'delete-bank-account',
                    })
                  )
                }
                fullWidth
              >
                {translate('edit_delete_bank_account')}
              </EditDeleteButton>
            </Box>
          }
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              px: 3,
              py: 2,
              height: '100%',
              maxHeight: '70vh',
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} textAlign="center" mb={2}>
              {translate('bank_details')}
            </Typography>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                pr: 1,
                /* Scrollbar styling */
                scrollbarWidth: 'thin', // Firefox
                scrollbarColor: '#dadada transparent',

                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'linear-gradient(180deg,#FFA726,#FFD54F)',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#FFA726',
                },
              }}
            >
              {banks.length === 0 ? (
                <Typography textAlign="center" color="text.secondary" fontSize="14px" sx={{ mt: 4 }}>
                  {translate('no_bank_accounts_added')}
                </Typography>
              ) : (
                banks.map((bank, index) => (
                  <BankCard key={bank.id} bank={bank} background={imageGradient[index % imageGradient.length]} />
                ))
              )}
            </Box>
          </Box>
        </CustomModal>
      )}

      {!isDesktop && (
        <Drawer
          anchor="right"
          maxWidth="xs"
          open={handleOpen}
          onClose={handleClose}
          PaperProps={{
            sx: {
              width: '100%',
            },
          }}
        >
          <Box
            sx={{
              px: 0.5,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
            }}
          >
            <IconButton onClick={handleClose}>
              <Iconify icon="material-symbols:arrow-back-ios-new-rounded" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {translate('bank-details')}
            </Typography>
            <IconButton onClick={() => setOpenBankAccount(true)}>
              <Image src={IconPlus} sx={{ width: '24px', height: '24px' }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              height: '100%',
              background: isSafariOrIOS ? theme.palette.gradients.theme3Ios : theme.palette.gradients.theme3,
            }}
          >
            <Box
              sx={{
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                backgroundColor: '#fff',
              }}
            >
              {banks.length === 0 ? (
                <Typography textAlign="center" color="text.secondary" fontSize="14px" sx={{ mt: 4 }}>
                  {translate('no_bank_accounts_added')}
                </Typography>
              ) : (
                banks.map((bank, index) => (
                  <BankCard key={bank.id} bank={bank} background={imageGradient[index % imageGradient.length]} />
                ))
              )}
            </Box>
            <Box display="flex" flexDirection="column" width="100%" gap={2} mt={2}>
              <AddBankButton onClick={() => setOpenBankAccount(true)} fullWidth>
                {translate('add_bank_account')}
              </AddBankButton>

              <EditDeleteButton onClick={() => setOpenDeleteBankAccount(true)} fullWidth>
                {translate('edit_delete_bank_account')}
              </EditDeleteButton>
            </Box>
          </Box>
        </Drawer>
      )}

      <DeleteBankAccount handleOpen={openDeleteBankAccount} handleClose={() => setOpenDeleteBankAccount(false)} />
      <AddBankAccountDialog handleClose={() => setOpenBankAccount(false)} handleOpen={openBankAccount} />
    </>
  );
};

export default BankDetailsDialog;
